import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  height: fit-content;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    if (props.$variant === 'success') return '#51cf66';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeapDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 800px;
`;

const LevelRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  align-items: center;
`;

const Node = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => {
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isSwapping) return '#ff6b6b';
    if (props.$isNew) return '#51cf66';
    if (props.$isRemoved) return '#868e96';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: #333;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  border: ${props => props.$isCurrent ? '3px solid #ff6b6b' : 'none'};
  position: relative;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const NodeIndex = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
`;

const ConnectionLine = styled.div`
  width: 2px;
  height: 20px;
  background: rgba(255, 255, 255, 0.6);
  margin: 0 auto;
`;

const ArrayDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ArrayTitle = styled.h3`
  color: white;
  margin: 0 0 15px 0;
  text-align: center;
  font-size: 1.3rem;
`;

const ArrayContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ArrayElement = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => {
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isSwapping) return '#ff6b6b';
    if (props.$isSorted) return '#51cf66';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: #333;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: ${props => props.$isCurrent ? '2px solid #ff6b6b' : 'none'};
  position: relative;
`;

const ElementIndex = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
`;

const InfoPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1.2rem;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const StatusBar = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatusText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const AlgorithmSelector = styled.select`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const StatsPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatsTitle = styled.h4`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1rem;
`;

const StatsText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
`;

function HeapPage() {
  const [heapType, setHeapType] = useState('max'); // 'max' or 'min'
  const [heap, setHeap] = useState([100, 50, 80, 30, 40, 60, 70]);
  const [inputValue, setInputValue] = useState('');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [currentNode, setCurrentNode] = useState(null);
  const [swappingNodes, setSwappingNodes] = useState([]);
  const [newNode, setNewNode] = useState(null);
  const [removedNode, setRemovedNode] = useState(null);
  const [sortedArray, setSortedArray] = useState([]);
  const [operationHistory, setOperationHistory] = useState([]);

  const heapTypes = {
    max: {
      name: 'Max Heap',
      description: 'Parent node is always greater than or equal to its children',
      complexity: 'Insert: O(log n), Delete: O(log n), Build: O(n)'
    },
    min: {
      name: 'Min Heap',
      description: 'Parent node is always less than or equal to its children',
      complexity: 'Insert: O(log n), Delete: O(log n), Build: O(n)'
    }
  };

  const getParentIndex = (index) => Math.floor((index - 1) / 2);
  const getLeftChildIndex = (index) => 2 * index + 1;
  const getRightChildIndex = (index) => 2 * index + 2;

  const hasParent = (index) => getParentIndex(index) >= 0;
  const hasLeftChild = (index) => getLeftChildIndex(index) < heap.length;
  const hasRightChild = (index) => getRightChildIndex(index) < heap.length;

  const getLeftChild = (index) => heap[getLeftChildIndex(index)];
  const getRightChild = (index) => heap[getRightChildIndex(index)];

  const swap = (index1, index2) => {
    const newHeap = [...heap];
    [newHeap[index1], newHeap[index2]] = [newHeap[index2], newHeap[index1]];
    setHeap(newHeap);
  };

  const shouldSwap = (parentIndex, childIndex) => {
    if (heapType === 'max') {
      return heap[parentIndex] < heap[childIndex];
    } else {
      return heap[parentIndex] > heap[childIndex];
    }
  };

  const heapifyUp = async (index) => {
    while (hasParent(index) && shouldSwap(getParentIndex(index), index)) {
      const parentIndex = getParentIndex(index);
      setSwappingNodes([index, parentIndex]);
      setCurrentStep(`Swapping ${heap[index]} with parent ${heap[parentIndex]}`);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      swap(index, parentIndex);
      index = parentIndex;
      
      await new Promise(resolve => setTimeout(resolve), 500);
    }
    setSwappingNodes([]);
  };

  const heapifyDown = async (index) => {
    while (hasLeftChild(index)) {
      let childIndex = getLeftChildIndex(index);
      
      if (hasRightChild(index)) {
        if (heapType === 'max') {
          childIndex = getRightChild(index) > getLeftChild(index) ? getRightChildIndex(index) : getLeftChildIndex(index);
        } else {
          childIndex = getRightChild(index) < getLeftChild(index) ? getRightChildIndex(index) : getLeftChildIndex(index);
        }
      }
      
      if (!shouldSwap(index, childIndex)) {
        break;
      }
      
      setSwappingNodes([index, childIndex]);
      setCurrentStep(`Swapping ${heap[index]} with child ${heap[childIndex]}`);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      swap(index, childIndex);
      index = childIndex;
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    setSwappingNodes([]);
  };

  const insert = async (value) => {
    if (!value || isVisualizing) return;
    
    setIsVisualizing(true);
    setCurrentStep(`Inserting ${value} into the heap`);
    
    const newHeap = [...heap, parseInt(value)];
    setHeap(newHeap);
    setNewNode(newHeap.length - 1);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setCurrentStep(`Heapifying up to maintain ${heapType} heap property`);
    await heapifyUp(newHeap.length - 1);
    
    setNewNode(null);
    setCurrentStep(`Inserted ${value} successfully`);
    
    // Add to operation history
    setOperationHistory(prev => [...prev, {
      type: 'insert',
      value: parseInt(value),
      timestamp: new Date().toLocaleTimeString()
    }]);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentStep('');
    setIsVisualizing(false);
    setInputValue('');
  };

  const removeRoot = async () => {
    if (heap.length === 0 || isVisualizing) return;
    
    setIsVisualizing(true);
    const rootValue = heap[0];
    setCurrentStep(`Removing root element: ${rootValue}`);
    
    // Replace root with last element
    const newHeap = [...heap];
    newHeap[0] = newHeap[newHeap.length - 1];
    newHeap.pop();
    setHeap(newHeap);
    setRemovedNode(0);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (newHeap.length > 0) {
      setCurrentStep(`Heapifying down to maintain ${heapType} heap property`);
      await heapifyDown(0);
    }
    
    setRemovedNode(null);
    setCurrentStep(`Removed ${rootValue} successfully`);
    
    // Add to operation history
    setOperationHistory(prev => [...prev, {
      type: 'remove',
      value: rootValue,
      timestamp: new Date().toLocaleTimeString()
    }]);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const buildHeap = async () => {
    if (isVisualizing) return;
    
    setIsVisualizing(true);
    setCurrentStep(`Building ${heapType} heap from array`);
    
    // Start from the last non-leaf node
    for (let i = Math.floor(heap.length / 2) - 1; i >= 0; i--) {
      setCurrentNode(i);
      setCurrentStep(`Heapifying down from index ${i}`);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      await heapifyDown(i);
    }
    
    setCurrentNode(null);
    setCurrentStep(`${heapType} heap built successfully`);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const heapSort = async () => {
    if (isVisualizing) return;
    
    setIsVisualizing(true);
    setCurrentStep('Starting heap sort');
    
    const heapCopy = [...heap];
    const sorted = [];
    
    // Build heap first
    setCurrentStep('Building heap for sorting');
    for (let i = Math.floor(heapCopy.length / 2) - 1; i >= 0; i--) {
      await heapifyDown(i);
    }
    
    // Extract elements one by one
    while (heapCopy.length > 0) {
      const root = heapCopy[0];
      sorted.push(root);
      
      setCurrentStep(`Extracting ${root} and adding to sorted array`);
      
      // Replace root with last element
      heapCopy[0] = heapCopy[heapCopy.length - 1];
      heapCopy.pop();
      
      if (heapCopy.length > 0) {
        await heapifyDown(0);
      }
      
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    
    setSortedArray(sorted);
    setCurrentStep(`Heap sort completed! Sorted array: [${sorted.join(', ')}]`);
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const generateRandomHeap = () => {
    const size = Math.floor(Math.random() * 10) + 5; // 5-15 elements
    const randomHeap = Array.from({ length: size }, () => Math.floor(Math.random() * 100) + 1);
    setHeap(randomHeap);
    setSortedArray([]);
    setOperationHistory([]);
    setCurrentStep('');
    setCurrentNode(null);
    setSwappingNodes([]);
    setNewNode(null);
    setRemovedNode(null);
  };

  const clearHeap = () => {
    setHeap([]);
    setSortedArray([]);
    setOperationHistory([]);
    setCurrentStep('');
    setCurrentNode(null);
    setSwappingNodes([]);
    setNewNode(null);
    setRemovedNode(null);
  };

  const renderHeap = () => {
    if (heap.length === 0) {
      return (
        <div style={{ color: 'white', textAlign: 'center', fontSize: '1.2rem' }}>
          Heap is empty
        </div>
      );
    }

    const levels = [];
    let currentLevel = 0;
    let nodesInLevel = 1;
    let nodeIndex = 0;

    while (nodeIndex < heap.length) {
      const levelNodes = [];
      for (let i = 0; i < nodesInLevel && nodeIndex < heap.length; i++) {
        const node = heap[nodeIndex];
        levelNodes.push(
          <Node
            key={nodeIndex}
            $isCurrent={currentNode === nodeIndex}
            $isSwapping={swappingNodes.includes(nodeIndex)}
            $isNew={newNode === nodeIndex}
            $isRemoved={removedNode === nodeIndex}
          >
            {node}
            <NodeIndex>{nodeIndex}</NodeIndex>
          </Node>
        );
        nodeIndex++;
      }
      
      levels.push(
        <LevelRow key={currentLevel}>
          {levelNodes}
        </LevelRow>
      );
      
      if (currentLevel > 0) {
        levels.splice(levels.length - 1, 0, <ConnectionLine key={`conn-${currentLevel}`} />);
      }
      
      currentLevel++;
      nodesInLevel *= 2;
    }

    return levels;
  };

  const renderArray = () => {
    return (
      <ArrayDisplay>
        <ArrayTitle>Array Representation</ArrayTitle>
        <ArrayContainer>
          {heap.map((value, index) => (
            <ArrayElement
              key={index}
              $isCurrent={currentNode === index}
              $isSwapping={swappingNodes.includes(index)}
              $isSorted={sortedArray.length > 0}
            >
              {value}
              <ElementIndex>{index}</ElementIndex>
            </ArrayElement>
          ))}
        </ArrayContainer>
      </ArrayDisplay>
    );
  };

  const getHeapHeight = () => Math.ceil(Math.log2(heap.length + 1));
  const getIsComplete = () => {
    const height = getHeapHeight();
    const expectedNodes = Math.pow(2, height) - 1;
    return heap.length === expectedNodes;
  };

  return (
    <PageContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Heap Visualizer</Title>
        <Subtitle>Visualize heap data structure operations and heap sort algorithm</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <VisualizationContainer>
            <HeapDisplay>
              {renderHeap()}
              {renderArray()}
            </HeapDisplay>
          </VisualizationContainer>
        </LeftPanel>
        
        <RightPanel>
          <ControlGroup>
            <Label>Heap Type</Label>
            <AlgorithmSelector
              value={heapType}
              onChange={(e) => setHeapType(e.target.value)}
              disabled={isVisualizing}
            >
              <option value="max">Max Heap</option>
              <option value="min">Min Heap</option>
            </AlgorithmSelector>
            
            <Label>Insert Value</Label>
            <Input
              type="number"
              placeholder="Enter a number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isVisualizing}
            />
            
            <Button
              onClick={() => insert(inputValue)}
              disabled={isVisualizing || !inputValue}
            >
              Insert
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Button
              onClick={removeRoot}
              disabled={isVisualizing || heap.length === 0}
              $variant="danger"
            >
              Remove Root
            </Button>
            
            <Button
              onClick={buildHeap}
              disabled={isVisualizing}
              $variant="success"
            >
              Build Heap
            </Button>
            
            <Button
              onClick={heapSort}
              disabled={isVisualizing}
              $variant="warning"
            >
              Heap Sort
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Button
              onClick={generateRandomHeap}
              disabled={isVisualizing}
              $variant="warning"
            >
              Generate Random Heap
            </Button>
            
            <Button
              onClick={clearHeap}
              disabled={isVisualizing}
              $variant="danger"
            >
              Clear Heap
            </Button>
          </ControlGroup>
          
          <InfoPanel>
            <InfoTitle>{heapTypes[heapType].name}</InfoTitle>
            <InfoText>{heapTypes[heapType].description}</InfoText>
            <InfoText><strong>Complexity:</strong> {heapTypes[heapType].complexity}</InfoText>
          </InfoPanel>
          
          <StatsPanel>
            <StatsTitle>Heap Statistics</StatsTitle>
            <StatsText><strong>Size:</strong> {heap.length} elements</StatsText>
            <StatsText><strong>Height:</strong> {getHeapHeight()}</StatsText>
            <StatsText><strong>Complete:</strong> {getIsComplete() ? 'Yes' : 'No'}</StatsText>
            <StatsText><strong>Root:</strong> {heap.length > 0 ? heap[0] : 'N/A'}</StatsText>
          </StatsPanel>
          
          <StatusBar>
            <StatusText><strong>Status:</strong> {isVisualizing ? 'Processing...' : 'Ready'}</StatusText>
            {currentStep && (
              <StatusText><strong>Current:</strong> {currentStep}</StatusText>
            )}
          </StatusBar>
          
          {operationHistory.length > 0 && (
            <InfoPanel>
              <InfoTitle>Operation History</InfoTitle>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {operationHistory.slice(-5).reverse().map((op, index) => (
                  <div key={index} style={{ marginBottom: '5px', fontSize: '0.8rem' }}>
                    <span style={{ color: op.type === 'insert' ? '#51cf66' : '#ff6b6b' }}>
                      {op.type.toUpperCase()}
                    </span>
                    : {op.value} {op.timestamp}
                  </div>
                ))}
              </div>
            </InfoPanel>
          )}
        </RightPanel>
      </MainContent>
    </PageContainer>
  );
}

export default HeapPage;
